import { useCallback, useEffect, useMemo, useState } from "react";
import type { AoeShape, AoeTemplate } from "@roll1/shared";
import { cellCenter } from "@roll1/shared/spell-aoe";
import type { UseAoeTemplatesReturn } from "./useAoeTemplates";

export type AoePlacementState = 'idle' | 'placing' | 'rotating';

export interface UseAoePlacementReturn {
  state: AoePlacementState;
  previewTemplate: Omit<AoeTemplate, 'id' | 'placedBy'> | null;
  startPlacement: (shape: AoeShape, sizeFt: number, color: string, label?: string, durationMs?: number) => void;
  cancelPlacement: () => void;
  handleMouseMove: (canvasX: number, canvasY: number, shiftKey: boolean, gridSize: number) => void;
  handleClick: (canvasX: number, canvasY: number, shiftKey: boolean, gridSize: number) => void;
  handleRightClick: () => void;
  handleKeyDown: (key: string) => void;
  handleWheel: (deltaY: number) => void;
}

const ROTATABLE_SHAPES = new Set<AoeShape>(['cone', 'line']);

function snapToGridCenter(px: number, gridSize: number): number {
  return cellCenter(px, gridSize);
}

/**
 * Interactive AoE placement tool.
 * State machine: idle -> placing -> (rotating for cone/line) -> placed.
 */
export function useAoePlacement(
  aoeTemplates: UseAoeTemplatesReturn,
): UseAoePlacementReturn {
  const [state, setState] = useState<AoePlacementState>('idle');
  const [pendingShape, setPendingShape] = useState<AoeShape>('sphere');
  const [pendingSizeFt, setPendingSizeFt] = useState(20);
  const [pendingColor, setPendingColor] = useState('rgba(255,0,0,0.3)');
  const [pendingLabel, setPendingLabel] = useState<string | undefined>(undefined);
  const [pendingDurationMs, setPendingDurationMs] = useState<number | undefined>(undefined);
  const [previewTemplate, setPreviewTemplate] = useState<Omit<AoeTemplate, 'id' | 'placedBy'> | null>(null);

  const startPlacement = useCallback((shape: AoeShape, sizeFt: number, color: string, label?: string, durationMs?: number) => {
    // Cylinder is visually identical to sphere on 2D — fallback for backward compat
    const resolvedShape = shape === 'cylinder' ? 'sphere' : shape;
    setPendingShape(resolvedShape);
    setPendingSizeFt(sizeFt);
    setPendingColor(color);
    setPendingLabel(label);
    setPendingDurationMs(durationMs);
    setState('placing');
    setPreviewTemplate({
      shape,
      originX: 0,
      originY: 0,
      sizeFt,
      rotation: 0,
      color,
      label,
    });
  }, []);

  const cancelPlacement = useCallback(() => {
    setState('idle');
    setPreviewTemplate(null);
  }, []);

  const handleMouseMove = useCallback((canvasX: number, canvasY: number, shiftKey: boolean, gridSize: number) => {
    if (state === 'idle') return;

    const originX = shiftKey ? canvasX : snapToGridCenter(canvasX, gridSize);
    const originY = shiftKey ? canvasY : snapToGridCenter(canvasY, gridSize);

    if (state === 'placing') {
      setPreviewTemplate(prev => prev ? { ...prev, originX, originY } : null);
    } else if (state === 'rotating' && previewTemplate) {
      // Calculate rotation angle from origin to mouse
      const dx = canvasX - previewTemplate.originX;
      const dy = canvasY - previewTemplate.originY;
      const angle = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
      setPreviewTemplate(prev => prev ? { ...prev, rotation: angle } : null);
    }
  }, [state, previewTemplate]);

  const handleClick = useCallback((canvasX: number, canvasY: number, shiftKey: boolean, gridSize: number) => {
    if (state === 'idle') return;

    if (state === 'placing') {
      const originX = shiftKey ? canvasX : snapToGridCenter(canvasX, gridSize);
      const originY = shiftKey ? canvasY : snapToGridCenter(canvasY, gridSize);

      if (ROTATABLE_SHAPES.has(pendingShape)) {
        // For cone/line, first click sets origin, then enter rotating state
        setPreviewTemplate(prev => prev ? { ...prev, originX, originY } : null);
        setState('rotating');
      } else {
        // For sphere/cube/cylinder, single click places
        aoeTemplates.placeTemplate({
          shape: pendingShape,
          originX,
          originY,
          sizeFt: pendingSizeFt,
          rotation: 0,
          color: pendingColor,
          label: pendingLabel,
        }, pendingDurationMs);
        setState('idle');
        setPreviewTemplate(null);
      }
    } else if (state === 'rotating' && previewTemplate) {
      // Second click confirms rotation
      const dx = canvasX - previewTemplate.originX;
      const dy = canvasY - previewTemplate.originY;
      const angle = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;

      aoeTemplates.placeTemplate({
        shape: pendingShape,
        originX: previewTemplate.originX,
        originY: previewTemplate.originY,
        sizeFt: pendingSizeFt,
        rotation: angle,
        color: pendingColor,
        label: pendingLabel,
      }, pendingDurationMs);
      setState('idle');
      setPreviewTemplate(null);
    }
  }, [state, pendingShape, pendingSizeFt, pendingColor, pendingLabel, pendingDurationMs, aoeTemplates, previewTemplate]);

  const handleRightClick = useCallback(() => {
    cancelPlacement();
  }, [cancelPlacement]);

  const handleKeyDown = useCallback((key: string) => {
    if (key === 'Escape') {
      cancelPlacement();
    }
  }, [cancelPlacement]);

  const handleWheel = useCallback((deltaY: number) => {
    if (state === 'rotating' && previewTemplate) {
      const delta = deltaY > 0 ? 5 : -5;
      setPreviewTemplate(prev => {
        if (!prev) return null;
        const newRotation = ((prev.rotation + delta) % 360 + 360) % 360;
        return { ...prev, rotation: newRotation };
      });
    }
  }, [state, previewTemplate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setState('idle');
      setPreviewTemplate(null);
    };
  }, []);

  return useMemo(() => ({
    state,
    previewTemplate,
    startPlacement,
    cancelPlacement,
    handleMouseMove,
    handleClick,
    handleRightClick,
    handleKeyDown,
    handleWheel,
  }), [state, previewTemplate, startPlacement, cancelPlacement, handleMouseMove, handleClick, handleRightClick, handleKeyDown, handleWheel]);
}
